import { FUNCIONES_PANEL, type SlugFuncion } from '@/components/tablero/funcionesPanel';
import { TEXTOS_GESTION } from '@/components/tablero/plan/textosPlan';

/*
 * D-21, D-22, D-31: la lista cerrada de lo que puede ser un panel hijo. El hijo vive en un único
 * parámetro de la URL del padre (`abrir=<tipo>:<id>`) y esa URL es entrada de usuario: la escribe o
 * la pega cualquiera que tenga el enlace. Por eso solo es hijo lo que esta tabla declara; cualquier
 * otro valor, incluidas las claves heredadas de Object (`__proto__`, `constructor`, `toString`), es
 * «sin hijo» (T-07-01, T-15-07).
 */

export const PARAMETRO_HIJO = 'abrir';

export type TipoHijo =
    | 'transcripcion'
    | 'documento'
    | 'documento-clinico'
    | 'plantilla-clinica'
    | 'psicometria'
    | 'manuscrito'
    | 'historial-plan';

export interface DefinicionTipoHijo {
    // Todos llevan un ObjectId salvo el historial del plan, que es del paciente entero.
    readonly conId: boolean;
    // Los cuatro tipos E son las funciones del asistente abiertas como hijo (D-16).
    readonly esE: boolean;
    readonly etiqueta: string;
    readonly slug: SlugFuncion | null;
}

function definir(conId: boolean, etiqueta: string, slug: SlugFuncion | null = null): DefinicionTipoHijo {
    return Object.freeze({ conId, esE: slug !== null, etiqueta, slug });
}

// D-31: las etiquetas de los tipos E salen de FUNCIONES_PANEL y la del historial de TEXTOS_GESTION,
// para que cada texto tenga una sola fuente.
export const TIPOS_HIJO: Readonly<Record<TipoHijo, DefinicionTipoHijo>> = Object.freeze({
    transcripcion: definir(true, 'Transcripción'),
    documento: definir(true, 'Documento'),
    'documento-clinico': definir(true, FUNCIONES_PANEL['clinical-document'].titulo, 'clinical-document'),
    'plantilla-clinica': definir(true, FUNCIONES_PANEL['clinical-template'].titulo, 'clinical-template'),
    psicometria: definir(true, FUNCIONES_PANEL.psychometry.titulo, 'psychometry'),
    manuscrito: definir(true, FUNCIONES_PANEL.handwritten.titulo, 'handwritten'),
    'historial-plan': definir(false, TEXTOS_GESTION.historial),
});

// Object.hasOwn y no `in`: el valor viene de la URL.
export function esTipoHijo(valor: string): valor is TipoHijo {
    return Object.hasOwn(TIPOS_HIJO, valor);
}

export function esHijoE(tipo: TipoHijo): boolean {
    return TIPOS_HIJO[tipo].esE;
}

export function tipoConId(tipo: TipoHijo): boolean {
    return TIPOS_HIJO[tipo].conId;
}

function mapearPorTipo<T>(extraer: (definicion: DefinicionTipoHijo) => T): Readonly<Record<TipoHijo, T>> {
    const entradas = (Object.keys(TIPOS_HIJO) as TipoHijo[]).map((tipo) => [tipo, extraer(TIPOS_HIJO[tipo])]);
    return Object.freeze(Object.fromEntries(entradas)) as Readonly<Record<TipoHijo, T>>;
}

export const ETIQUETA_HIJO: Readonly<Record<TipoHijo, string>> = mapearPorTipo((definicion) => definicion.etiqueta);

const ENTRADAS_SLUG = (Object.keys(TIPOS_HIJO) as TipoHijo[]).flatMap((tipo) => {
    const slug = TIPOS_HIJO[tipo].slug;
    return slug === null ? [] : [[tipo, slug] as const];
});

export const SLUG_FUNCION_DE_HIJO: Readonly<Partial<Record<TipoHijo, SlugFuncion>>> = Object.freeze(Object.fromEntries(ENTRADAS_SLUG));
